import React, { useState, CSSProperties } from "react";
import { useTranslation, Trans } from "react-i18next";
import { useMutation } from "react-query";
import { recruitingAPI } from "../services/recruitingApi";
import "./CVGeneration.css";

interface JobSpecificCVGenerationProps {
  applicationId: string | number;
  candidateName: string;
  jobTitle: string;
  onClose: () => void;
}

interface CVContent {
  content?: string;
  cvContent?: string;
}

interface CVResponse {
  success?: boolean;
  data?: CVContent;
}

const JobSpecificCVGeneration: React.FC<JobSpecificCVGenerationProps> = ({
  applicationId,
  candidateName,
  jobTitle,
  onClose,
}) => {
  const { t } = useTranslation(["recruiting", "common"]);
  const [generatedCV, setGeneratedCV] = useState<CVContent | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [previewMode, setPreviewMode] = useState(true); // Start in preview mode to show rendered HTML

  // Generate job-specific CV mutation
  const generateCVMutation = useMutation<CVResponse, Error, string | number>(
    async (appId) => {
      const response = (await recruitingAPI.generateJobSpecificCV(
        appId,
      )) as CVResponse;
      return response;
    },
    {
      onSuccess: (response) => {
        // Handle nested response structure: response.data contains the cvResult
        if ((response?.success && response.data) || response?.data?.content) {
          // The response.data is the cvResult from backend which has { success, format, content, metadata }
          setGeneratedCV(response.data || null);
          setError(null);
        } else {
          setError(t("jobSpecificCV.failedToGenerate"));
        }
      },
      onError: (
        err: Error & {
          response?: { data?: { error?: string; message?: string } };
        },
      ) => {
        console.error("Error generating job-specific CV:", err);
        setError(
          err.response?.data?.error ||
            err.response?.data?.message ||
            err.message ||
            t("jobSpecificCV.failedToGenerate"),
        );
      },
    },
  );

  const handleGenerate = () => {
    setError(null);
    setGeneratedCV(null);
    generateCVMutation.mutate(applicationId);
  };

  const handleDownload = () => {
    if (!generatedCV) return;

    // Handle nested structure: generatedCV might be response.data which contains content
    const cvContent = generatedCV.content || generatedCV.cvContent;

    const content = typeof cvContent === "string" ? cvContent : "";
    const filename = `${candidateName.replaceAll(/\s+/g, "_")}_CV_${jobTitle.replaceAll(/\s+/g, "_")}.html`;
    const mimeType = "text/html";

    const blob = new Blob([content], { type: mimeType });
    const url = URL.createObjectURL(blob);
    const link = document.createElement("a");
    link.href = url;
    link.download = filename;
    document.body.appendChild(link);
    link.click();
    link.remove();
    URL.revokeObjectURL(url);
  };

  const handlePrint = () => {
    if (!generatedCV) {
      alert(t("jobSpecificCV.noCvToPrint"));
      return;
    }

    const cvContent = generatedCV.content || generatedCV.cvContent;
    const content = typeof cvContent === "string" ? cvContent : "";

    const printWindow = window.open("", "_blank");
    if (printWindow) {
      printWindow.document.write(content);
      printWindow.document.close();
      printWindow.focus();
      printWindow.print();
    }
  };

  const handlePreview = () => {
    setPreviewMode(!previewMode);
  };

  const renderCVContent = () => {
    if (!generatedCV) return null;

    const cvContent = generatedCV.content || generatedCV.cvContent;
    const content = typeof cvContent === "string" ? cvContent : "";

    const preStyle: CSSProperties = {
      whiteSpace: "pre-wrap",
      wordWrap: "break-word",
      padding: "1rem",
      backgroundColor: "#f5f5f5",
      borderRadius: "4px",
      overflow: "auto",
      maxHeight: "600px",
    };

    // Always render HTML in preview mode (iframe), show code only when user clicks "Show Code"
    if (previewMode) {
      return (
        <div className="cv-preview-container">
          <iframe
            srcDoc={content}
            title="CV Preview"
            className="cv-preview-iframe"
            style={{ width: "100%", height: "100%", border: "none" }}
          />
        </div>
      );
    } else {
      return (
        <pre className="cv-content-text" style={preStyle}>
          {content}
        </pre>
      );
    }
  };

  return (
    <div
      className="modal-overlay"
      onClick={onClose}
      onKeyDown={(e) => e.key === "Escape" && onClose()}
      aria-hidden="true"
    >
      <dialog
        className="modal large-modal"
        onClick={(e) => e.stopPropagation()}
        open
        aria-labelledby="cv-generation-title"
      >
        <div className="modal-header">
          <h2 id="cv-generation-title">
            {t("jobSpecificCV.title", { name: candidateName })}
          </h2>
          <button
            className="btn-close"
            onClick={onClose}
            aria-label={t("common:actions.close")}
          >
            ×
          </button>
        </div>
        <div className="modal-content">
          <div className="cv-generation-content">
            {jobTitle && (
              <div className="job-info-banner">
                <strong>{t("jobSpecificCV.jobLabel")}</strong> {jobTitle}
              </div>
            )}

            {!generatedCV && (
              <>
                <div className="cv-info-section">
                  <p>
                    <Trans
                      i18nKey="recruiting:jobSpecificCV.infoText"
                      values={{ jobTitle }}
                      components={{ 1: <strong /> }}
                    />
                  </p>
                  <ul>
                    <li>{t("jobSpecificCV.infoPoint1")}</li>
                    <li>{t("jobSpecificCV.infoPoint2")}</li>
                    <li>{t("jobSpecificCV.infoPoint3")}</li>
                  </ul>
                </div>

                {error && <div className="error-message">{error}</div>}

                <div className="cv-actions">
                  <button
                    onClick={handleGenerate}
                    disabled={generateCVMutation.isLoading}
                    className="btn-primary"
                  >
                    {generateCVMutation.isLoading
                      ? t("common:loading.processing")
                      : t("recruiting:candidates.detail.buttons.generateCV")}
                  </button>
                  <button onClick={onClose} className="btn-secondary">
                    {t("common:actions.cancel")}
                  </button>
                </div>

                {generateCVMutation.isLoading && (
                  <div className="loading-indicator">
                    <div className="spinner"></div>
                    <p>{t("common:loading.processing")}</p>
                  </div>
                )}
              </>
            )}

            {generatedCV && (
              <>
                <div className="cv-actions-header">
                  <div className="format-info">
                    <Trans
                      i18nKey="recruiting:jobSpecificCV.formatInfo"
                      components={{ 1: <strong /> }}
                    />
                  </div>
                  <div className="cv-action-buttons">
                    <button onClick={handlePreview} className="btn btn-outline">
                      {previewMode
                        ? t("cvGeneration.showCode")
                        : t("cvGeneration.preview")}
                    </button>
                    <button onClick={handleDownload} className="btn-primary">
                      {t("cvGeneration.download")}
                    </button>
                    <button onClick={handlePrint} className="btn-outline">
                      {t("cvGeneration.print")}
                    </button>
                    <button
                      onClick={() => {
                        setGeneratedCV(null);
                        setError(null);
                        setPreviewMode(true); // Reset to preview mode
                      }}
                      className="btn btn-outline"
                    >
                      {t("jobSpecificCV.generateNew")}
                    </button>
                    <button onClick={onClose} className="btn btn-secondary">
                      {t("common:actions.close")}
                    </button>
                  </div>
                </div>

                <div className="cv-content-container">{renderCVContent()}</div>
              </>
            )}
          </div>
        </div>
      </dialog>
    </div>
  );
};

export default JobSpecificCVGeneration;
